import type { Routes } from "@angular/router";

import { AuthGuard } from "./core/guard/auth.guard";
import { RoleGuard } from "./core/guard/role.guard";
import { WmsWriteGuard } from "./core/guard/wms-write.guard";
import { TranslationsResolver } from "./core/resolvers/translations.resolver";
import { ForbiddenComponent } from "./features/forbidden/forbidden.component";
import { NotFoundComponent } from "./features/not-found/not-found.component";
import { gridDetailResolver } from "./features/supplier/detail/grid-detail/grid-detail.resolver";
import { supplierDetailResolver } from "./features/supplier/detail/supplier-detail.resolver";
import { pickingOrderResolver } from "./features/wms/warehouse/orders/picking-workflow/picking-order.resolver";
import { warehouseResolver } from "./features/wms/warehouse/warehouse.resolver";
import { LayoutComponent } from "./layout/layout.component";
import { APP_PATHS } from "./shared/constants";

export const routes: Routes = [
  {
    path: "",
    component: LayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: APP_PATHS.home,
        data: { section: "dashboard" },
        canActivate: [RoleGuard],
        resolve: { translationsLoaded: TranslationsResolver },
        loadComponent: () =>
          import("./features/dashboard/dashboard.component").then((m) => m.DashboardComponent),
      },
      {
        path: APP_PATHS.supplierList,
        data: { section: "supplier", allowedRoles: ["PRODUCT_MANAGER"] },
        canActivate: [RoleGuard],
        resolve: { translationsLoaded: TranslationsResolver },
        loadComponent: () =>
          import("./features/supplier/supplier-list.component").then(
            (m) => m.SupplierListComponent,
          ),
      },
      {
        path: APP_PATHS.supplierDetail,
        data: { section: "supplier/detail", allowedRoles: ["PRODUCT_MANAGER"] },
        canActivate: [RoleGuard],
        resolve: { translationsLoaded: TranslationsResolver, supplier: supplierDetailResolver },
        loadComponent: () =>
          import("./features/supplier/detail/supplier-detail.component").then(
            (m) => m.SupplierDetailComponent,
          ),
      },
      {
        path: APP_PATHS.gridDetail,
        data: { section: "supplier/detail", allowedRoles: ["PRODUCT_MANAGER"] },
        canActivate: [RoleGuard],
        resolve: {
          translationsLoaded: TranslationsResolver,
          grid: gridDetailResolver,
          supplier: supplierDetailResolver,
        },
        loadComponent: () =>
          import("./features/supplier/detail/grid-detail/grid-detail.component").then(
            (m) => m.GridDetailComponent,
          ),
      },
      {
        path: APP_PATHS.warehouse,
        data: { section: "wms/warehouse" },
        canActivate: [RoleGuard],
        resolve: {
          translationsLoaded: TranslationsResolver,
          warehouse: warehouseResolver,
        },
        loadComponent: () =>
          import("./features/wms/warehouse/warehouse.component").then((m) => m.WarehouseComponent),
      },
      {
        path: APP_PATHS.ordersHistoryById,
        data: { section: "wms/warehouse/orders", tab: "HISTORY" },
        canActivate: [RoleGuard],
        resolve: { translationsLoaded: TranslationsResolver, warehouse: warehouseResolver },
        loadComponent: () =>
          import("./features/wms/warehouse/orders/orders.component").then((m) => m.OrdersComponent),
      },
      {
        path: APP_PATHS.ordersHistory,
        data: { section: "wms/warehouse/orders", tab: "HISTORY" },
        canActivate: [RoleGuard],
        resolve: { translationsLoaded: TranslationsResolver, warehouse: warehouseResolver },
        loadComponent: () =>
          import("./features/wms/warehouse/orders/orders.component").then((m) => m.OrdersComponent),
      },
      {
        path: APP_PATHS.ordersToShipById,
        data: { section: "wms/warehouse/orders", tab: "TO_SHIP" },
        canActivate: [RoleGuard],
        resolve: {
          translationsLoaded: TranslationsResolver,
          warehouse: warehouseResolver,
        },
        loadComponent: () =>
          import("./features/wms/warehouse/orders/orders.component").then((m) => m.OrdersComponent),
      },
      {
        path: APP_PATHS.ordersToShip,
        data: { section: "wms/warehouse/orders", tab: "TO_SHIP" },
        canActivate: [RoleGuard],
        resolve: {
          translationsLoaded: TranslationsResolver,
          warehouse: warehouseResolver,
        },
        loadComponent: () =>
          import("./features/wms/warehouse/orders/orders.component").then((m) => m.OrdersComponent),
      },
      {
        path: APP_PATHS.orders,
        data: { section: "wms/warehouse/orders" },
        canActivate: [RoleGuard],
        resolve: {
          translationsLoaded: TranslationsResolver,
          warehouse: warehouseResolver,
        },
        loadComponent: () =>
          import("./features/wms/warehouse/orders/orders.component").then((m) => m.OrdersComponent),
      },
      {
        path: APP_PATHS.returnsById,
        data: { section: "wms/warehouse/returns" },
        canActivate: [WmsWriteGuard],
        resolve: { translationsLoaded: TranslationsResolver },
        loadComponent: () =>
          import("./features/wms/warehouse/returns/return-entry/return-entry.component").then(
            (m) => m.ReturnEntryComponent,
          ),
      },
      {
        path: APP_PATHS.returns,
        data: { section: "wms/warehouse/returns" },
        canActivate: [WmsWriteGuard],
        resolve: { translationsLoaded: TranslationsResolver },
        loadComponent: () =>
          import("./features/wms/warehouse/returns/returns.component").then(
            (m) => m.ReturnsComponent,
          ),
      },
      {
        path: APP_PATHS.zoneInventoryZone,
        data: { section: "wms/warehouse/zone-inventory" },
        canActivate: [WmsWriteGuard],
        resolve: { translationsLoaded: TranslationsResolver },
        loadComponent: () =>
          import("./features/wms/stocks/zone-inventory/zone-count/zone-count.component").then(
            (m) => m.ZoneCountComponent,
          ),
      },
      {
        path: APP_PATHS.zoneInventory,
        data: { section: "wms/warehouse/zone-inventory" },
        canActivate: [WmsWriteGuard],
        resolve: { translationsLoaded: TranslationsResolver },
        loadComponent: () =>
          import("./features/wms/stocks/zone-inventory/zones/zones.component").then(
            (m) => m.ZonesComponent,
          ),
      },
      {
        path: APP_PATHS.inventory,
        data: { section: "wms/stocks/inventory" },
        canActivate: [RoleGuard],
        resolve: { translationsLoaded: TranslationsResolver },
        loadComponent: () =>
          import("./features/wms/stocks/inventory/inventory.component").then(
            (m) => m.InventoryComponent,
          ),
      },
      {
        path: APP_PATHS.stockTracking,
        data: { section: "wms/stocks/stock-tracking" },
        canActivate: [RoleGuard],
        resolve: { translationsLoaded: TranslationsResolver },
        loadComponent: () =>
          import("./features/wms/stocks/stock-tracking/stock-tracking.component").then(
            (m) => m.StockTrackingComponent,
          ),
      },
      {
        path: APP_PATHS.stockRelocation,
        data: {
          section: "wms/warehouse/stock-relocation",
          breadcrumb: "wms/warehouse/stock-relocation",
        },
        canActivate: [RoleGuard],
        resolve: { translationsLoaded: TranslationsResolver },
        loadComponent: () =>
          import("./features/wms/stocks/stock-relocation/stock-relocation.component").then(
            (m) => m.StockRelocationComponent,
          ),
      },
      {
        path: APP_PATHS.suppliers,
        data: {
          section: "wms/procurement/suppliers",
        },
        canActivate: [RoleGuard],
        resolve: { translationsLoaded: TranslationsResolver },
        loadComponent: () =>
          import("./features/wms/procurement/suppliers/suppliers.component").then(
            (m) => m.SuppliersComponent,
          ),
      },
      {
        path: APP_PATHS.userProfile,
        data: { section: "user-profile" },
        resolve: { translationsLoaded: TranslationsResolver },
        loadComponent: () =>
          import("./features/user-profile/user-profile.component").then(
            (m) => m.UserProfileComponent,
          ),
      },
    ],
  },
  {
    path: APP_PATHS.pickingOrder,
    data: { section: "wms/warehouse/orders/picking-order" },
    canActivate: [AuthGuard, RoleGuard],
    resolve: {
      translationsLoaded: TranslationsResolver,
      orderLoaded: pickingOrderResolver,
    },
    loadComponent: () =>
      import("./features/wms/warehouse/orders/picking-order/picking-order.component").then(
        (m) => m.PickingOrderComponent,
      ),
  },
  {
    path: APP_PATHS.picking,
    data: { section: "wms/warehouse/picking" },
    canActivate: [AuthGuard, RoleGuard],
    resolve: {
      translationsLoaded: TranslationsResolver,
      orderLoaded: pickingOrderResolver,
    },
    loadComponent: () =>
      import("./features/wms/warehouse/orders/picking-workflow/picking-workflow.component").then(
        (m) => m.PickingWorkflowComponent,
      ),
  },
  {
    path: "403",
    component: ForbiddenComponent,
  },
  {
    path: "**",
    canActivate: [AuthGuard],
    component: NotFoundComponent,
  },
];
